import { useCallback, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { sleep } from '@chainflip/utils/async';
import { type ChainflipAsset } from '@chainflip/utils/chainflip';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import classNames from 'classnames';
import { type ChainflipToken } from '@/shared/assets/tokens';
import { SkeletonLine } from '@/shared/components/atoms/LoadingSkeleton';
import Button from '@/shared/components/flip-ui-kit/Button';
import { BorrowPosition } from '@/shared/components/lending/BorrowPosition';
import { LendingPoolsTable } from '@/shared/components/lending/LendingPoolsTable';
import { LiquidationsTable } from '@/shared/components/lending/LiquidationsTable';
import useBoolean from '@/shared/hooks/useBoolean';
import { useLendingPools } from '@/shared/hooks/useLendingPools';
import { useLiquidations } from '@/shared/hooks/useLiquidations';
import { type Loan } from '@/shared/hooks/useLpLoanAccount';
import { useLpBalancesAndAddresses } from '@/shared/hooks/useLpStats';
import { usePolkadot } from '@/shared/hooks/usePolkadot';
import useSettingsStore from '@/shared/hooks/useSettingsStore';
import BorrowIcon from '@/shared/icons/flip-ui-kit/large/Borrow';
import PlusCircle from '@/shared/icons/flip-ui-kit/large/PlusCircle';
import StopIcon from '@/shared/icons/flip-ui-kit/large/Stop';
import { chainflipAssetMap } from '@/shared/utils/env';
import { LIQUIDATIONS_QUERY } from '@/shared/utils/lending';
import { formatUsdValue } from '@/shared/utils/numbers';
import { BorrowModal } from './Modals/BorrowModal';
import { BorrowTourModal } from './Modals/BorrowTourCompleted';
import { ManualLiquidationModal } from './Modals/ManualLiquidationModal';
import { RemoveSupplyModal } from './Modals/RemoveSupplyModal';
import { RepayModal } from './Modals/RepayModal';
import { SupplyModal } from './Modals/SupplyModal';
import useLoanAccount from '../../hooks/useLoanAccount';
import { useExtrinsicSDK } from '../../hooks/useStateChainAccount';
import { LpFundAccountButton } from '../LpFundAccountButton';

export default function MyBorrowPositionContent() {
  const router = useRouter();
  const queryClient = useQueryClient();
  const { borrowTourStep, isBorrowTourComplete, shouldIntroduceLending, setSetting } =
    useSettingsStore();
  const {
    value: isBorrowTourModalOpened,
    setTrue: setIsBorrowTourModalOpened,
    setFalse: setIsBorrowTourModalClosed,
  } = useBoolean(false);

  const {
    value: supplyModalOpened,
    setTrue: setSupplyModalOpened,
    setFalse: setSupplyModalClosed,
  } = useBoolean(false);

  const {
    value: removeSupplyModalOpened,
    setTrue: setRemoveSupplyModalOpened,
    setFalse: setRemoveSupplyModalClosed,
  } = useBoolean(false);

  const {
    value: borrowModalOpened,
    setTrue: setBorrowModalOpened,
    setFalse: setBorrowModalClosed,
  } = useBoolean(false);

  const {
    value: repayModalOpened,
    setTrue: setRepayModalOpened,
    setFalse: setRepayModalClosed,
  } = useBoolean(false);

  const [borrowSelectedToken, setBorrowSelectedToken] = useState<ChainflipToken>(
    chainflipAssetMap.Usdc,
  );
  const [supplySelectedToken, setSupplySelectedToken] = useState<ChainflipToken>(
    chainflipAssetMap.Btc,
  );
  const [selectedLoan, setSelectedLoan] = useState<Loan>();
  const [supplyOpenedFromBorrow, setSupplyOpenedFromBorrow] = useState(false);
  const { selectedAccount } = usePolkadot(true);
  const { loanAccount, isLoading: isLoanAccountLoading } = useLoanAccount();
  const { totalAvailableLiquidityUsd, isLoading: isLendingPoolsLoading } = useLendingPools();
  const { toast, sendExtrinsic: stopVoluntaryLiquidation } = useExtrinsicSDK(
    'stopVoluntaryLiquidation',
  );
  const { hasLiquidity } = useLpBalancesAndAddresses(selectedAccount?.idSs58);

  const { loans, isLiquidating, collateralBalances, totalCollateralBalanceUsd } = loanAccount;

  const { liquidationsData } = useLiquidations({
    idSs58: selectedAccount.idSs58,
  });

  const {
    value: manualLiquidationModalOpen,
    setTrue: setManualLiquidationModalOpened,
    setFalse: setManualLiquidationModalClosed,
  } = useBoolean(false);

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (
      !isBorrowTourComplete &&
      !selectedAccount.readonly &&
      !shouldIntroduceLending &&
      hasLiquidity
    ) {
      timeout = setTimeout(() => {
        setIsBorrowTourModalOpened();
      }, 500);
    }
    return () => clearTimeout(timeout);
  }, [isBorrowTourComplete, selectedAccount.readonly, shouldIntroduceLending, hasLiquidity]);

  const hasLiquidations = liquidationsData?.allLiquidationSwapRequests?.nodes.length;
  const hasInProgressVoluntaryLiquidations = loans.some(
    (loan) => loan.status === 'SoftVoluntaryLiquidation',
  );

  const updatedLoan = loans.find((loan) => loan.id === selectedLoan?.id);

  const openSupplyModal = (token: ChainflipToken, fromBorrow = false) => {
    setSupplySelectedToken(token);
    setSupplyOpenedFromBorrow(fromBorrow);
    setSupplyModalOpened();
  };

  const openRemoveSupplyModal = (token: ChainflipToken) => {
    setSupplySelectedToken(token);
    setRemoveSupplyModalOpened();
  };

  const openBorrowModal = (token: ChainflipToken) => {
    setBorrowSelectedToken(token);
    setBorrowModalOpened();
  };

  const openRepayModal = (loan: Loan) => {
    setSelectedLoan(loan);
    setRepayModalOpened();
  };

  const RowButton = useCallback(
    // eslint-disable-next-line react/no-unused-prop-types
    ({ asset }: { asset: ChainflipAsset }) => (
      <LpFundAccountButton
        size="small"
        type="primary-standard"
        skipFundingCheck={totalCollateralBalanceUsd > 0}
        disabled={isLoanAccountLoading || isLendingPoolsLoading || isLiquidating}
        onClick={(e) => {
          e.stopPropagation();
          if (totalCollateralBalanceUsd > 0) {
            openBorrowModal(chainflipAssetMap[asset]);
          } else {
            openSupplyModal(chainflipAssetMap.Btc, true);
            setBorrowSelectedToken(chainflipAssetMap[asset]);
          }
        }}
      >
        Borrow
      </LpFundAccountButton>
    ),
    [
      openBorrowModal,
      totalCollateralBalanceUsd,
      openSupplyModal,
      setBorrowSelectedToken,
      setSetting,
      isLoanAccountLoading,
      isLendingPoolsLoading,
      isLiquidating,
    ],
  );

  const isReadonlyAccount = selectedAccount.readonly;

  const { mutate, isPending: isStopVoluntaryLiquidationPending } = useMutation({
    mutationFn: async () => stopVoluntaryLiquidation(),
    async onSuccess() {
      await sleep(500);
      await Promise.all(
        [LIQUIDATIONS_QUERY, 'cf_loan_accounts'].map((query) =>
          queryClient.invalidateQueries({ queryKey: [query], exact: false }),
        ),
      );
      toast.success(`Liquidation stopped successfully`);
    },
    onError(err) {
      toast.error('Error while submitting transaction', { description: err.message });
    },
  });

  return (
    <div className="flex flex-col gap-y-6">
      <SupplyModal
        open={supplyModalOpened}
        onClose={() => {
          setSupplyModalClosed();
        }}
        selectedToken={supplySelectedToken}
        setSelectedToken={setSupplySelectedToken}
        openedFromBorrow={supplyOpenedFromBorrow}
        onBorrowClick={setBorrowModalOpened}
      />
      <RemoveSupplyModal
        open={removeSupplyModalOpened}
        onClose={() => {
          setRemoveSupplyModalClosed();
        }}
        selectedToken={supplySelectedToken}
        setSelectedToken={setSupplySelectedToken}
      />
      <BorrowModal
        onAddSupplyClick={() => openSupplyModal(chainflipAssetMap.Btc)}
        open={borrowModalOpened}
        onClose={setBorrowModalClosed}
        selectedToken={borrowSelectedToken}
        setSelectedToken={setBorrowSelectedToken}
      />
      {selectedLoan && (
        <RepayModal
          open={repayModalOpened}
          onClose={setRepayModalClosed}
          afterLeave={() => setSelectedLoan(undefined)}
          initialLoan={selectedLoan}
          currentLoan={updatedLoan}
        />
      )}
      <div className="flex justify-between font-aeonikMedium text-cf-light-4">
        {/* eslint-disable-next-line no-nested-ternary */}
        {isLoanAccountLoading || isLendingPoolsLoading ? (
          <SkeletonLine width={275} height={30} />
        ) : loans.length > 0 || collateralBalances.length > 0 ? (
          <div className="text-24">My Borrow position</div>
        ) : (
          <div className="w-[372px] text-[32px] leading-[36px]">
            Borrow{' '}
            <span className="text-cf-orange-2">
              up to {formatUsdValue(totalAvailableLiquidityUsd, false)}
            </span>{' '}
            against your assets
          </div>
        )}
        <div className="flex gap-x-4">
          <LpFundAccountButton
            className={classNames(borrowTourStep === 1 && 'z-[2147483647]')}
            onClick={() => openSupplyModal(chainflipAssetMap.Btc)}
            disabled={isLoanAccountLoading || isLendingPoolsLoading}
            type="secondary-standard"
            icon={<PlusCircle />}
            iconPos="left"
          >
            Add Supply
          </LpFundAccountButton>
          <LpFundAccountButton
            icon={<BorrowIcon />}
            iconPos="right"
            className={classNames(borrowTourStep === 2 && 'z-[2147483647]')}
            skipFundingCheck={totalCollateralBalanceUsd > 0}
            disabled={isLoanAccountLoading || isLendingPoolsLoading || isLiquidating}
            onClick={() => {
              if (totalCollateralBalanceUsd > 0) {
                setBorrowModalOpened();
              } else {
                openSupplyModal(chainflipAssetMap.Btc, true);
              }
            }}
          >
            Borrow
          </LpFundAccountButton>
        </div>
      </div>
      <BorrowPosition
        loanAccount={loanAccount}
        loanAccountLoading={isLoanAccountLoading}
        openRepayModal={isReadonlyAccount ? undefined : openRepayModal}
        openSupplyModal={isReadonlyAccount ? undefined : openSupplyModal}
        openRemoveSupplyModal={isReadonlyAccount ? undefined : openRemoveSupplyModal}
        openBorrowModal={isReadonlyAccount ? undefined : openBorrowModal}
        openManualLiquidationModal={isReadonlyAccount ? undefined : setManualLiquidationModalOpened}
        SupplyButton={LpFundAccountButton}
      />
      {(hasLiquidations || hasInProgressVoluntaryLiquidations) && (
        <div className="flex flex-col gap-y-6">
          <div className="flex justify-between">
            <div className="font-aeonikMedium text-24 text-cf-white">Liquidations</div>
            {hasInProgressVoluntaryLiquidations && (
              <Button
                type="primary-standard"
                icon={<StopIcon />}
                iconPos="left"
                onClick={() => mutate()}
                disabled={isStopVoluntaryLiquidationPending}
              >
                <span className="ml-1">Stop Liquidation</span>
              </Button>
            )}
          </div>
          <LiquidationsTable idSs58={selectedAccount.idSs58} showAccountId={false} />
        </div>
      )}
      <div className="font-aeonikMedium text-24 text-cf-white">Markets</div>
      <LendingPoolsTable
        Button={RowButton}
        onRowClick={(chainflipAsset) => router.push(`/lending/markets/${chainflipAsset}`)}
      />
      <BorrowTourModal
        open={isBorrowTourModalOpened}
        onClose={setIsBorrowTourModalClosed}
        onComplete={() => {
          openSupplyModal(chainflipAssetMap.Btc);
        }}
      />
      <ManualLiquidationModal
        open={manualLiquidationModalOpen}
        onClose={setManualLiquidationModalClosed}
      />
    </div>
  );
}
